import type { Study } from '../api/client.ts';
import { BriefSynthesisStatus } from '../artifacts/BriefSynthesisStatus.tsx';
import { fieldingStage, isAnalysisInProgress } from '../artifacts/fielding-stage.ts';
import {
  ANALYSING_HEADLINES,
  ANALYZING_DONE_LINE,
  FIELDWORK_FINISHED,
  READINESS_ESCALATION_MESSAGES,
  RESEARCH_QUESTIONS_LEAD,
} from '../artifacts/launch-copy.ts';
import { PhaseStrip } from '../artifacts/PhaseStrip.tsx';
import type { BriefSynthesisRead } from '../artifacts/use-brief-synthesis.ts';
import { useBoundedPoll } from '../artifacts/use-bounded-poll.ts';
import { Pending } from '../ui/Pending.tsx';
import { LIVE_POLL_MS } from './FieldingStep.tsx';
import { useResearchQuestions } from './use-research-questions.ts';

export interface AnalyzingStepProps {
  studyId: string;
  study: Study | null;
  /**
   * Asks the shell to read the study again. The watch calls it, and the shell's read is what moves
   * the researcher on to the Brief once it is written.
   */
  onStudyChanged: () => void;
  /**
   * The study's Brief request, read by the shell (`useBriefSynthesis`) while the study is analysed
   * and its analysis not given up on. The headline and the synthesis status line are both drawn
   * from it, as is the shell's Next hint.
   */
  brief: BriefSynthesisRead;
}

/**
 * What happens to the answers once fieldwork is over: Sigora reads them and writes the Brief.
 *
 * The step watches the study while it is `analyzing`, by asking the shell to read it again; that
 * read is what moves the researcher on to the Brief once it is published. On a `complete` study,
 * reached only by going back to it or by a link, it says the Brief is written and watches nothing:
 * the footer's Next opens the Brief.
 */
export const AnalyzingStep = ({ studyId, study, onStudyChanged, brief }: AnalyzingStepProps) => {
  const state = study?.state ?? null;
  /**
   * The poller's ending, and only while the study is where that ending is about. A study that
   * reached `complete` was analysed after all, and an escalation left over from an earlier attempt
   * must not be what a finished study reports.
   */
  const escalation = state === 'analyzing' ? (study?.readinessEscalation ?? null) : null;
  /**
   * Where the analysis has got to, once its Brief request has been read; `null` before that, and
   * for an escalated study, whose headline says why the wait stopped.
   */
  const stage =
    state === 'analyzing' && escalation === null && brief.loaded
      ? fieldingStage(brief.synthesis)
      : null;

  /**
   * The wait while a study is `analyzing`: **not bounded**, and not stopped by an escalation
   * either.
   *
   * Everything this step shows about an analysed study — a state change, or the readiness poller
   * giving up — **the server writes**, so there is no ending this page has to time out in order to
   * discover. A bound here would have to agree with the server's readiness timeout, and that
   * agreement would live in neither file; having no bound removes the coupling.
   *
   * **And an escalated study keeps being watched.** An escalation is withdrawable — the poller
   * clears it when a redelivered job finds the analysis moving after all, because an outage is a
   * thing that ends — so a page that stopped reading on seeing one could never observe the
   * withdrawal, and would hold the sentence it exists to retract for the life of the tab. The cost
   * of being wrong in this direction is re-reading a finished study; the cost of being wrong in the
   * other is a sentence on screen the server has already taken back.
   */
  useBoundedPoll({
    active: state === 'analyzing',
    intervalMs: LIVE_POLL_MS,
    // No bound. The hook compares its tick count against this, so nothing here ever exhausts and
    // the returned flag is dead — deliberately not bound to a name, because a flag that is
    // structurally always false is a claim waiting for somebody to render it.
    limit: Number.POSITIVE_INFINITY,
    onTick: onStudyChanged,
  });

  /**
   * Whether the answers are actually being read now (`isAnalysisInProgress`, or before the first
   * request read lands), which is the only time the step shows a spinner. Not once the analysis was
   * given up on, nor once the Brief failed or was declined: every such ending says what happened
   * instead, so no spinner outlives its cause.
   */
  const inProgress =
    state === 'analyzing' && escalation === null && (stage === null || isAnalysisInProgress(stage));
  const questions = useResearchQuestions({ studyId, active: inProgress });

  if (study === null || (state !== 'analyzing' && state !== 'complete')) {
    return null;
  }

  return (
    <section className="launch-panel analyzing-step" data-test-id="analyzing-step">
      <PhaseStrip settingUp={false} state={state} />

      {state === 'complete' ? (
        <p data-test-id="analyzing-done">{ANALYZING_DONE_LINE}</p>
      ) : escalation !== null ? (
        <p className="chat-error" data-test-id="launch-launched" role="alert">
          {READINESS_ESCALATION_MESSAGES[escalation.escalation]}
        </p>
      ) : inProgress ? (
        <Pending className="analyzing-headline" testId="launch-launched">
          {stage === null ? FIELDWORK_FINISHED : ANALYSING_HEADLINES[stage]}
        </Pending>
      ) : (
        <p data-test-id="launch-launched">
          {stage === null ? FIELDWORK_FINISHED : ANALYSING_HEADLINES[stage]}
        </p>
      )}

      {inProgress && questions !== null && questions.length > 0 && (
        <div className="fielding-questions" data-test-id="fielding-questions">
          <p>{RESEARCH_QUESTIONS_LEAD}</p>
          <ol>
            {questions.map((question) => (
              <li data-test-id="fielding-question" key={question.id}>
                {question.text}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/*
        Where writing the Brief has got to, under the headline: only while the study is being
        analysed and its analysis has not been given up on, since the Brief is asked for once the
        analysis is ready. Drawn from the same read as the headline.
      */}
      {state === 'analyzing' && escalation === null && <BriefSynthesisStatus brief={brief} />}
    </section>
  );
};
